import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import DataTable from "examples/Tables/DataTable";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ToastContainer, toast } from "react-toastify";
import { Stack, CircularProgress } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import Papa from "papaparse";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import MDBadge from "components/MDBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import colors from "assets/theme/base/colors";
import "../campus/Campus.css";
import Select from "react-select";
import nodata from "assets/images/nodata.png";
import { TextField } from "@mui/material";
import { hasAdmissionPermission } from "hooks/permissions";
import TodayIcon from "@mui/icons-material/Today";

const { info } = colors;
const BASE_URL = process.env.REACT_APP_BASE_URL;
const token = sessionStorage.getItem("token");

const UserLoginReport = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Session Storage
  const user = JSON.parse(sessionStorage.getItem("user"));
  const organization_id = user ? user.organization_id : null;

  // Variable Declarations
  const [allLoginReports, setAllLoginReports] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [originalReports, setOriginalReports] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  
  // Filter states
  const [selectedRole, setSelectedRole] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Helper function to format date as YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to get today's date and tomorrow's date
  const getTodayDates = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return {
      today: formatDateToYYYYMMDD(today),
      tomorrow: formatDateToYYYYMMDD(tomorrow)
    };
  };

  const handleSearchKeywordChange = (e) => {
    const searchTerm = e.target.value.trim().toLowerCase();
    setSearchKeyword(searchTerm);
    
    if (!searchTerm) {
      setAllLoginReports(originalReports);
      return;
    }

    const filteredReports = originalReports.filter((report) => {
      return (
        (report.id && report.id.toString().toLowerCase().includes(searchTerm)) ||
        (report.username && report.username.toLowerCase().includes(searchTerm)) ||
        (report.email && report.email.toLowerCase().includes(searchTerm)) ||
        (report.role_name && report.role_name.toLowerCase().includes(searchTerm)) ||
        (report.login_time && report.login_time.toLowerCase().includes(searchTerm))
      );
    });

    setAllLoginReports(filteredReports);
  };

  const handleExportCSV = () => {
    if (allLoginReports.length === 0) return;
    
    // Define the headers for the CSV
    const headers = [
      "Sr.No", 
      "Username",
      "Email",
      "Role",
      "Login Time",
    ];

    // Map the rows to include all necessary fields
    const data = allLoginReports.map((report, index) => ({
      serial: index + 1,
      username: report.name,
      email: report.email,
      role_name: report.role,
      login_time: report.loginAt,
    }));

    // Convert to CSV format
    const csv = Papa.unparse([headers, ...data.map((row) => Object.values(row))]);

    // Create a blob and download the CSV
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "UserLoginReport.csv";
    link.click();
  };

  // Handle Today button click
  const handleTodayClick = () => {
    const { today, tomorrow } = getTodayDates();
    setFromDate(today);
    setToDate(tomorrow);
    
    // Auto-fetch data if role is selected
    if (selectedRole) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        getLoginReportWithDates(selectedRole.value, today, tomorrow);
      }, 100);
    }
  };

  // API calls
  const fetchAllRoles = async () => {
    setLoadingRoles(true);
    try {
      const requestOptions = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };
      const response = await fetch(BASE_URL + `/auth/get_roles`, requestOptions);
      const data = await response.json();
      
      if (response.ok) {
        setAllRoles(data.roles || []);
      } else {
        toast.error(data.error || "Failed to fetch roles");
      }
    } catch (error) {
      console.log("Error fetching roles:", error);
      toast.error("An error occurred while fetching roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  // Modified getLoginReport function to use parameters for Today button
  const getLoginReportWithDates = async (roleId, fromDateParam, toDateParam) => {
    setLoading(true);
    
    // Prepare filter data
    const filterData = {
      role_id: roleId,
      from_date: fromDateParam,
      to_date: toDateParam
    };

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(filterData),
      };

      const response = await fetch(
        BASE_URL + `/auth/get_login_report`,
        requestOptions
      );
      const result = await response.json();
      
      if (response.ok) {
        const reports = result || [];
        setAllLoginReports(reports);
        setOriginalReports(reports);
        
        toast.success(`${reports.length} login records found`);
      } else {
        toast.error(result.error || "Failed to fetch login report");
        setAllLoginReports([]);
        setOriginalReports([]);
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("An error occurred while fetching login report");
      setAllLoginReports([]);
      setOriginalReports([]);
    } finally {
      setLoading(false);
    }
  };

  const getLoginReport = async () => {
    // Validation
    if (!selectedRole) {
      toast.error("Please select a role");
      return;
    }

    if (!fromDate) {
      toast.error("Please select from date");
      return;
    }

    if (!toDate) {
      toast.error("Please select to date");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be greater than to date");
      return;
    }

    await getLoginReportWithDates(selectedRole.value, fromDate, toDate);
  };

  // Handle role selection change
  const handleRoleChange = (selectedOption) => {
    setSelectedRole(selectedOption);
  };

  // Handle date changes
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
  };

  // Table Configuration
  const columns = [
    { Header: "Sr.No", accessor: "serial", align: "center" },
    { Header: "Username", accessor: "username", align: "center" },
    { Header: "Email", accessor: "email", align: "center" },
    { Header: "Role", accessor: "role_name", align: "center" },
    { Header: "Login Time", accessor: "login_time", align: "center" },
  ];

  const rows = allLoginReports.length > 0
    ? allLoginReports.map((report, index) => ({
        serial: index + 1,
        username: report.name,
        email: report.email,
        role_name: report.role,
        login_time: new Date(report.loginAt).toLocaleString(),
      }))
    : [
        {
          serial: null,
          username: null,
          email: null,
          role_name: null,
          login_time: null,
        },
      ];

  // Initial data loading
  useEffect(() => {
    fetchAllRoles();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ToastContainer />
      <MDBox pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card className="mb-4">
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium" mb={2}>
                  User Login Report Filters
                </MDTypography>
                <Grid container spacing={2}>
                  {/* Role Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      Role
                    </MDTypography>
                    <Select
                      name="role_id"
                      options={allRoles.map((role) => ({
                        value: role.id,
                        label: role.name,
                      }))}
                      className="basic-select"
                      classNamePrefix="select"
                      placeholder={loadingRoles ? "Loading roles..." : "Select Role"}
                      value={selectedRole}
                      onChange={handleRoleChange}
                      isLoading={loadingRoles}
                      isDisabled={loadingRoles}
                      isClearable
                    />
                  </Grid>

                  {/* From Date Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      From Date
                    </MDTypography>
                    <TextField
                      type="date"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* To Date Filter */}
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="subtitle2" fontWeight="light" mb={1}>
                      To Date
                    </MDTypography>
                    <TextField
                      type="date"
                      value={toDate}
                      onChange={handleToDateChange}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                      size="small"
                    />
                  </Grid>

                  {/* Action Buttons */}
                  <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleTodayClick}
                      startIcon={<TodayIcon />}
                      style={{ color: "white" }}
                      sx={{ mt: 2 }}
                    >
                      Today
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={getLoginReport}
                      disabled={loading}
                      sx={{ mt: 2 }}
                      style={{color: "white"}}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Fetch Login Report"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>

            <Card>
              <MDBox
                mx={0}
                mt={0}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                  sx={{ width: "100%" }}
                >
                  <MDTypography variant="h6" fontWeight="medium">
                    User Login Report
                  </MDTypography>

                  <Stack direction="row" spacing={2}>
                    <Paper component="form" className="search-paper">
                      <InputBase
                        sx={{ ml: 1, flex: 1, fontSize: "16px" }}
                        placeholder="Search"
                        inputProps={{ "aria-label": "search login reports" }}
                        value={searchKeyword}
                        onChange={handleSearchKeywordChange}
                      />
                      <SearchIcon className="search-icon" />
                    </Paper>
                    {hasAdmissionPermission("UserLoginReport", "EXPORT CSV") && (
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={<DocumentScannerIcon />}
                        onClick={handleExportCSV}
                        style={{color: "white"}}
                      >
                        Export CSV
                      </Button>
                    )}
                  </Stack>
                </Stack>
              </MDBox>
              
              <MDBox pt={3} pb={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress />
                  </MDBox>
                ) : allLoginReports && allLoginReports.length > 0 ? (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" p={5}>
                    <img
                      src={nodata}
                      style={{
                        width: "20%",
                        height: "20%",
                      }}
                      alt="No Data"
                    />
                    <MDTypography variant="body1" mt={2}>
                      No login records found. Please adjust your filters and try again.
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default UserLoginReport;